import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, ArrowRight, LogOut, LayoutGrid } from "lucide-react";
import { NAV_LINKS } from "@/lib/site";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkClass = ({ isActive }) =>
    `relative text-sm font-medium transition-colors hover:text-foreground ${
      isActive ? "text-foreground" : "text-muted-foreground"
    }`;

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "border-b border-border bg-background/70 glass" : "border-b border-transparent bg-transparent"
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-5 sm:px-8">
        <Logo imgClass="h-7 sm:h-8" />

        <div className="hidden items-center gap-7 lg:flex">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkClass} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <ThemeToggle className="hidden sm:inline-flex" />
          {user && typeof user === "object" ? (
            <>
              <Link
                to="/app"
                data-testid="nav-workspace-btn"
                className="hidden items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-sm font-medium transition-colors hover:border-foreground/30 sm:inline-flex"
              >
                <LayoutGrid className="h-4 w-4" /> Workspace
              </Link>
              <button
                data-testid="nav-logout-btn"
                onClick={() => { logout(); navigate("/"); }}
                className="hidden items-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground sm:inline-flex"
              >
                <LogOut className="h-4 w-4" /> Logout
              </button>
            </>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login-link"
              className="hidden rounded-full px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground sm:inline-flex"
            >
              Login
            </Link>
          )}

          <Link
            to="/signup"
            data-testid="nav-start-free-btn"
            className="group hidden items-center gap-1.5 rounded-full bg-foreground px-4 py-2 text-sm font-semibold text-background transition-all hover:-translate-y-0.5 hover:shadow-lg sm:inline-flex"
          >
            Start Free
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>

          {/* Mobile */}
          <Sheet>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-btn"
                aria-label="Open menu"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card lg:hidden"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[86%] max-w-sm border-border bg-background">
              <div className="mb-8 flex items-center justify-between">
                <Logo imgClass="h-7" />
                <ThemeToggle />
              </div>
              <div className="flex flex-col gap-1">
                {NAV_LINKS.map((l) => (
                  <SheetClose asChild key={l.to}>
                    <NavLink
                      to={l.to}
                      data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="rounded-xl px-3 py-3 text-base font-medium text-foreground transition-colors hover:bg-muted"
                    >
                      {l.label}
                    </NavLink>
                  </SheetClose>
                ))}
              </div>
              <div className="mt-6 flex flex-col gap-3 border-t border-border pt-6">
                {user && typeof user === "object" ? (
                  <>
                    <SheetClose asChild>
                      <Link to="/app" className="rounded-full border border-border px-4 py-3 text-center text-sm font-medium">Workspace</Link>
                    </SheetClose>
                    <SheetClose asChild>
                      <button onClick={() => { logout(); navigate("/"); }} className="rounded-full px-4 py-3 text-center text-sm font-medium text-muted-foreground">Logout</button>
                    </SheetClose>
                  </>
                ) : (
                  <SheetClose asChild>
                    <Link to="/login" data-testid="mobile-login-link" className="rounded-full border border-border px-4 py-3 text-center text-sm font-medium">Login</Link>
                  </SheetClose>
                )}
                <SheetClose asChild>
                  <Link to="/signup" data-testid="mobile-start-free-btn" className="rounded-full bg-foreground px-4 py-3 text-center text-sm font-semibold text-background">Start Free</Link>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
}
